/**
 * @fileoverview End-to-end CAVE document processing workflow.
 *
 * Orchestrates the complete flow: upload → poll status → fetch artifact summary
 * → download artifact data → normalize → auto-resolve conflicts. Handles all
 * error states (failed, partial results, warnings) with appropriate user messaging.
 */

import pollDocumentStatus from './status';
import { fetchArtifactSummary, downloadArtifactData } from './artifacts';
import {
  extractProcessingFailureDetail,
  extractProcessingWarnings,
  createCavePartialResultError,
  CAVE_SCAN_STATUS,
} from './errors';
import { normalizeArtifacts } from '../domain/normalize';
import { autoResolveArtifacts } from '../domain/conflictDetection';
import {
  VETERAN_INFO_FIELDS,
  MILITARY_HISTORY_FIELDS,
} from '../domain/fieldMapping';

const ALL_FIELDS = [...VETERAN_INFO_FIELDS, ...MILITARY_HISTORY_FIELDS];

const ARTIFACT_TYPES = {
  DD214: 'DD214',
  DEATH: 'death',
};

// Global 6-minute budget across status polling, artifact summary, and all downloads
const GLOBAL_TIMEOUT_MS = 360000;

const normalizeType = type => (type || '').toLowerCase();

/**
 * Fetches all artifacts (DD-214 and death certificate forms) from a processed document.
 *
 * Retrieves the artifact summary, filters forms by type, downloads all artifact data
 * in parallel, and validates that all forms were successfully downloaded.
 *
 * @async
 * @param {string} documentId - The ID of the processed document
 * @param {function} [onStatusChange] - Callback(statusString) for progress updates
 * @param {number} globalDeadline - Absolute timestamp (ms) marking the deadline
 * @param {function} [shouldAbort] - Function that returns true if operation should abort
 * @returns {Promise<{caveSubmissionIds: Array<string>, dd214: Array, deathCertificates: Array}>}
 *   Grouped artifact data keyed by type
 * @throws {Error} If no forms extracted, any form download failed, or deadline exceeded
 */
export const fetchDocumentArtifacts = async (
  documentId,
  onStatusChange,
  globalDeadline,
  shouldAbort,
) => {
  const summary = await fetchArtifactSummary(
    documentId,
    {},
    globalDeadline,
    shouldAbort,
  );
  const forms = summary?.forms || [];

  // Empty-forms: the summary succeeded but the service extracted nothing usable.
  // This is NOT a silent success — surface it so callers can inform the user
  // that automated review produced no data.
  if (forms.length === 0) {
    throw createCavePartialResultError(
      'Automated review finished but did not extract any recognized ' +
        'information from this document. Enter your details manually.',
    );
  }

  // Signal that we are now moving into the artifact download phase.
  onStatusChange?.('downloading-artifacts');

  const dd214Forms = forms.filter(
    form =>
      normalizeType(form?.artifactType) === normalizeType(ARTIFACT_TYPES.DD214),
  );
  const deathForms = forms.filter(
    form =>
      normalizeType(form?.artifactType) === normalizeType(ARTIFACT_TYPES.DEATH),
  );

  const [dd214Data, deathData] = await Promise.all([
    Promise.all(
      dd214Forms.map(form =>
        downloadArtifactData(
          documentId,
          form?.mmsArtifactValidationId,
          {},
          globalDeadline,
          shouldAbort,
        ),
      ),
    ),
    Promise.all(
      deathForms.map(form =>
        downloadArtifactData(
          documentId,
          form?.mmsArtifactValidationId,
          {},
          globalDeadline,
          shouldAbort,
        ),
      ),
    ),
  ]);

  const retrieved = [...dd214Data, ...deathData].filter(Boolean);
  const totalForms = dd214Forms.length + deathForms.length;

  // Data-loss guard: the summary listed forms to download, but at least one
  // download came back empty/null. Previously `.filter(Boolean)` swallowed the
  // missing ones and returned artifact arrays that looked like a clean success,
  // so the Veteran saw a "successful" automated review even though data was
  // lost. Throw a partial-result error for ANY shortfall (all-failed OR
  // some-failed) so it routes through useCaveManager's catch → the per-file
  // "automated review not completed" alert with a manual-entry path.
  if (retrieved.length < totalForms) {
    throw createCavePartialResultError(
      'Automated review identified documents but could not retrieve all of ' +
        'the extracted data. Enter your details manually.',
    );
  }

  return {
    caveSubmissionIds: retrieved.map(o => o?.cave_submission_id),
    dd214: dd214Data.map(o => o?.raw_payload).filter(Boolean),
    deathCertificates: deathData.map(o => o?.raw_payload).filter(Boolean),
  };
};

/**
 * Processes a document end-to-end with automatic conflict resolution.
 *
 * Executes the complete workflow: polls for processing completion, fetches and
 * downloads artifacts, normalizes them to canonical form, then auto-resolves
 * any artifacts that match the current form data (avoiding unnecessary conflicts).
 *
 * Distinguishes between failure modes:
 * - `failed` (scanStatus): Hard terminal error with user message
 * - `completed_with_errors`: Soft failure with warnings/partial data
 * - Other: Unexpected status, not a success
 * - Success: Returns resolved idpArtifacts for integration into form
 *
 * @async
 * @param {{id: string, bucket: string, pdfKey: string}} contract - Upload contract
 * @param {object} formData - Current form state for conflict resolution
 * @param {Array} [existingFiles] - Files uploaded in previous sessions
 * @param {object} [options={}] - Workflow configuration
 * @param {object} [options.polling] - Polling options (passed to pollDocumentStatus)
 * @param {function} [options.shouldAbort] - Abort signal
 * @param {function} [options.onStatusChange] - Status change callback
 * @returns {Promise<object>} The resolved idpArtifacts for the processed document
 * @throws {Error} On processing failure, partial results, warnings, or deadline exceeded
 */
export const processDocumentWithAutoResolve = async (
  contract,
  formData,
  existingFiles,
  options = {},
) => {
  if (!contract?.id) {
    throw new Error('Invalid contract: missing document id.');
  }

  const globalDeadline = Date.now() + GLOBAL_TIMEOUT_MS;

  const status = await pollDocumentStatus(
    contract.id,
    options.polling,
    globalDeadline,
    options.shouldAbort,
  );
  const scanStatus = status?.scanStatus;

  // `failed` is a hard terminal error: surface the contract's user_message /
  // error detail if present, otherwise a clear generic message.
  if (scanStatus === CAVE_SCAN_STATUS.FAILED) {
    throw new Error(
      extractProcessingFailureDetail(status) ||
        'Automated document review failed. Enter your details manually.',
    );
  }

  // `completed_with_errors` is a DISTINCT terminal state: the document was
  // processed but the service reported warnings/partial extraction. Treat it as
  // an actionable failure with its own messaging (including any warnings) so it
  // is never mistaken for a clean success.
  if (scanStatus === CAVE_SCAN_STATUS.COMPLETED_WITH_ERRORS) {
    const warnings = extractProcessingWarnings(status);
    const detail = extractProcessingFailureDetail(status);
    const base =
      detail ||
      'Automated review finished with warnings and may be incomplete. ' +
        'Review and enter your details manually.';
    const message = warnings.length ? `${base} (${warnings.join('; ')})` : base;
    throw createCavePartialResultError(message);
  }

  // Any other non-completed status (e.g. an unexpected value that slipped past
  // polling) is not a success.
  if (scanStatus !== CAVE_SCAN_STATUS.COMPLETED) {
    throw new Error(
      extractProcessingFailureDetail(status) ||
        'Document processing did not complete successfully.',
    );
  }

  const sections = await fetchDocumentArtifacts(
    contract.id,
    options.onStatusChange,
    globalDeadline,
    options.shouldAbort,
  );
  const normalized = normalizeArtifacts(sections);

  const tempFile = { idpArtifacts: normalized };
  const allFiles = [...(existingFiles ?? []), tempFile];
  const resolved = autoResolveArtifacts(formData, allFiles, ALL_FIELDS);
  return resolved[resolved.length - 1].idpArtifacts;
};
